import {
  AfterViewInit,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { HttpErrorResponse } from '@angular/common/http';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { EditorComponent } from 'ngx-monaco-editor-v2';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import {
  WorkflowStatus,
  WorkflowTemplate,
} from '@nimmsta/ng-commons-lib/models';
import { WorkflowTemplateService } from '../../services/workflow-template.service';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';

_('error.workflow.invalid_json');
_('error.workflow.no_definition');

@Component({
  selector: 'app-workflow-template-definition',
  templateUrl: './workflow-template-definition.component.html',
  imports: [
    ErrorComponent,
    MatProgressSpinner,
    EditorComponent,
    ReactiveFormsModule,
    FormsModule,
    TranslatePipe,
  ],
})
export class WorkflowTemplateDefinitionComponent
  implements OnInit, AfterViewInit
{
  protected readonly modalRef = inject(DynamicDialogRef);
  private readonly dialogConfig = inject(
    DynamicDialogConfig<{ workflow: WorkflowTemplate }>,
  );
  private workflowService = inject(WorkflowTemplateService);

  workflow!: WorkflowTemplate;

  loading = true;

  definition?: string;

  error?: HttpErrorResponse;

  editorOptions = {
    automaticLayout: true,
    theme: 'vs-dark',
    language: 'json',
    fontSize: '14',
    wordWrap: 'on',
    scrollbar: {
      alwaysConsumeMouseWheel: false,
    },
  };

  @ViewChild('editor') editor!: EditorComponent;

  @ViewChild('lineWrap') lineWrap!: ElementRef;

  lineWrapBoolean = true;

  LINE_WRAP_KEY = 'line_wrap';

  ngOnInit(): void {
    this.workflow = this.dialogConfig.data?.workflow;
    if (!localStorage.getItem(this.LINE_WRAP_KEY)) {
      localStorage.setItem(this.LINE_WRAP_KEY, this.lineWrapBoolean.toString());
    }
    if (!this.workflow) {
      this.modalRef.close();
    } else {
      this.workflowService.getDefinition(this.workflow).subscribe({
        next: (definition: string) => {
          this.definition = JSON.stringify(definition, null, 2);
          this.loading = false;
        },
        error: (error: HttpErrorResponse) => {
          this.error = error;
          this.loading = false;
        },
      });
    }
  }

  ngAfterViewInit(): void {
    this.lineWrapBoolean = localStorage.getItem(this.LINE_WRAP_KEY) === 'true';
    this.lineWrap.nativeElement.checked = this.lineWrapBoolean;
    this.lineWrap.nativeElement.addEventListener('change', () => {
      this.lineWrapBoolean = this.lineWrap.nativeElement.checked;
      localStorage.setItem(this.LINE_WRAP_KEY, this.lineWrapBoolean.toString());
      this.editorOptions.wordWrap = this.lineWrapBoolean ? 'on' : 'off';
      this.editor.options = this.editorOptions;
    });
  }

  save(): void {
    if (this.definition) {
      try {
        JSON.parse(this.definition);
        this.loading = true;
        this.workflowService
          .updateDefinition(this.workflow, this.definition)
          .subscribe({
            next: () => {
              this.modalRef.close();
              this.loading = false;
            },
            error: (error: HttpErrorResponse) => {
              this.error = error;
              this.loading = false;
            },
          });
      } catch (error) {
        this.error = new HttpErrorResponse({
          statusText: 'error.workflow.invalid_json',
          error: error,
        });
      }
    } else {
      this.error = new HttpErrorResponse({
        statusText: 'error.workflow.no_definition',
      });
    }
  }

  isActive(): boolean {
    return this.workflow.status === WorkflowStatus.ACTIVE;
  }
}
